/**
 * Comparator bar — the app's signature element.
 *
 * A Taylor K-2006 doesn't give you a number, it gives you a *position*: you hold
 * the vial against a printed colour band and read where it lands. That is the
 * mental model this app is built on, so it is the visual primitive too — a
 * scale, an ideal zone, and a marker showing where this reading sits.
 *
 * It replaces status pills entirely. A pill tells you a category; the bar tells
 * you the category *and* how far off you are *and* which direction to move —
 * which is what you actually need before deciding whether to reach for a jug.
 *
 * The same shape becomes the target band on the charts, so the whole app reads
 * as one system (see docs/adr/0006).
 */

import { useId } from "react";

export type ComparatorStatus = "ok" | "low" | "high" | "missing";

interface Props {
  value: number | null;
  scaleMin: number;
  scaleMax: number;
  /** Target zone. Absent until targets are known (Phase 2) — the bar then
   *  shows scale and position only, which is still useful orientation. */
  ideal?: { low: number; high: number } | null;
  /**
   * Verdict from the server. When given it wins over the bar's own
   * ideal-zone comparison, because the two use different bounds: the server
   * judges against the outer acceptable range while the ideal zone is the
   * narrower band to aim for. Letting the bar decide for itself would mean a
   * value sitting between the two (calcium at 240 against an acceptable floor
   * of 220 and an ideal floor of 250) is coloured as a problem while the API
   * calls it fine.
   */
  status?: ComparatorStatus;
  /**
   * The outer acceptable bounds, drawn as ticks outside the ideal zone.
   *
   * Distinct from `ideal` on purpose, because they mean genuinely different
   * things and the bar was previously unable to say so. `ideal` is the band to
   * aim for; these are the lines the water should not cross. Free chlorine at
   * CYA 157 sits in a 12.5 / 15.5–17.5 / — arrangement, and without the floor
   * drawn, "13.0, just outside the green" and "13.0, half a ppm off the floor"
   * look identical.
   *
   * Either side may be null. Free chlorine genuinely has no published
   * maximum — the chart is minimum, target and SLAM — so drawing one would be
   * inventing a bound.
   */
  bounds?: { min: number | null; max: number | null } | null;
  color?: string;
  size?: "sm" | "md";
  label?: string;
}

/**
 * The span the bar covers, widened so the target always fits on it.
 *
 * `scaleMin`/`scaleMax` in params.ts are constants chosen for an ordinary
 * pool, and free chlorine's was 0–15. At CYA 157 the aim band is 15.5–17.5 —
 * the entire target off the right-hand edge, the ideal zone clamped to a
 * sliver at the end, and a reading of 13.0 rendered as though it were nearly
 * off the top of the scale when it is in fact below target. Exactly the bug
 * the CYA scale comment in params.ts describes, one parameter over.
 *
 * Rather than raising the constant — which fixes this pool until CYA moves
 * again — the scale follows whatever bounds it was given, with a tenth of the
 * span as breathing room so a marker sitting on the maximum is still visible.
 */
export function scaleFor(
  scaleMin: number,
  scaleMax: number,
  ideal?: { low: number; high: number } | null,
  bounds?: { min: number | null; max: number | null } | null,
  value?: number | null,
): { min: number; max: number } {
  const marks = [scaleMin, scaleMax, ideal?.low, ideal?.high, bounds?.min, bounds?.max, value].filter(
    (n): n is number => typeof n === "number" && Number.isFinite(n),
  );

  const low = Math.min(...marks);
  const high = Math.max(...marks);
  const pad = (high - low) * 0.1 || 1;
  return {
    // Never pad below the configured floor: a pH bar starting at 6.3 because
    // of padding reads as a scale nobody chose, and no measurement here is
    // meaningful below its parameter's own scaleMin.
    min: Math.min(scaleMin, low),
    max: high > scaleMax ? high + pad : scaleMax,
  };
}

const clampFraction = (value: number, min: number, max: number) =>
  Math.min(1, Math.max(0, (value - min) / (max - min)));

export function comparatorStatus(
  value: number | null,
  ideal?: { low: number; high: number } | null,
): ComparatorStatus {
  if (value === null) return "missing";
  if (!ideal) return "ok";
  if (value < ideal.low) return "low";
  if (value > ideal.high) return "high";
  return "ok";
}

const STATUS_COLOR: Record<ComparatorStatus, string> = {
  ok: "var(--color-status-ok)",
  low: "var(--color-status-low)",
  high: "var(--color-status-high)",
  missing: "var(--color-status-missing)",
};

export default function ComparatorBar({
  value,
  scaleMin,
  scaleMax,
  ideal = null,
  bounds = null,
  status: statusOverride,
  color,
  size = "md",
  label,
}: Props) {
  const titleId = useId();
  const status = statusOverride ?? comparatorStatus(value, ideal);
  const trackHeight = size === "sm" ? 8 : 12;

  const scale = scaleFor(scaleMin, scaleMax, ideal, bounds, value);
  const pct = (n: number) => clampFraction(n, scale.min, scale.max) * 100;

  const markerPct = value === null ? null : pct(value);
  const idealLeft = ideal ? pct(ideal.low) : null;
  const idealWidth = ideal && idealLeft !== null ? pct(ideal.high) - idealLeft : null;

  // Only drawn where they sit outside the ideal zone. A minimum that coincides
  // with the bottom of the aim band is the same line twice, and two marks on
  // one position reads as a range rather than as emphasis.
  const boundMarks = [
    bounds?.min != null && (!ideal || bounds.min < ideal.low) ? { at: bounds.min, key: "min" } : null,
    bounds?.max != null && (!ideal || bounds.max > ideal.high) ? { at: bounds.max, key: "max" } : null,
  ].filter((mark): mark is { at: number; key: string } => mark !== null);

  // The marker colour carries the verdict; the series colour is used only when
  // there is no target to judge against, so colour never means two things.
  const markerColor = ideal ? STATUS_COLOR[status] : (color ?? "var(--color-ink)");

  // The floor belongs in the spoken description too. A screen reader hearing
  // "13, target 15.5 to 17.5, ok" is told the reading is fine and never told
  // how close it is to the line that matters.
  const floor = bounds?.min != null ? `, minimum ${bounds.min}` : "";
  const ceiling = bounds?.max != null ? `, maximum ${bounds.max}` : "";
  const description =
    value === null
      ? `${label ?? "Value"} not measured`
      : ideal
        ? `${label ?? "Value"} ${value}, target ${ideal.low} to ${ideal.high}${floor}${ceiling}, ${status}`
        : `${label ?? "Value"} ${value} on a scale of ${scale.min} to ${scale.max}`;

  return (
    <div
      role="img"
      aria-labelledby={titleId}
      className="relative w-full select-none"
      style={{ height: trackHeight + 8 }}
    >
      <span id={titleId} className="sr-only">
        {description}
      </span>

      {/* Track */}
      <div
        className="absolute inset-x-0 rounded-full"
        style={{
          top: 4,
          height: trackHeight,
          background: "var(--color-surface-sunken)",
          border: "1px solid var(--color-border-subtle)",
        }}
      />

      {/* Ideal zone */}
      {idealLeft !== null && idealWidth !== null && (
        <div
          className="absolute rounded-full"
          style={{
            top: 4,
            height: trackHeight,
            left: `${idealLeft}%`,
            width: `${Math.max(idealWidth, 1.5)}%`,
            background: "color-mix(in oklch, var(--color-status-ok) 30%, transparent)",
            borderTop: "1px solid color-mix(in oklch, var(--color-status-ok) 55%, transparent)",
            borderBottom: "1px solid color-mix(in oklch, var(--color-status-ok) 55%, transparent)",
          }}
        />
      )}

      {/* Acceptable bounds. Drawn as short ticks inside the track rather than
          as a second filled zone: the fill means "aim here", and a second one
          would compete with it. A tick reads as a line not to cross, which is
          what a floor is. */}
      {boundMarks.map((mark) => (
        <div
          key={mark.key}
          aria-hidden
          className="absolute"
          style={{
            top: 4,
            height: trackHeight,
            width: 2,
            left: `calc(${pct(mark.at)}% - 1px)`,
            background: "color-mix(in oklch, var(--color-status-low) 70%, transparent)",
          }}
        />
      ))}

      {/* Marker. Overhangs the track so it stays legible against the ideal
          zone's fill, and eases when the value changes as you type. */}
      {markerPct !== null && (
        <div
          className="absolute rounded-full transition-[left] duration-150 ease-out"
          style={{
            top: 0,
            height: trackHeight + 8,
            width: 3,
            left: `calc(${markerPct}% - 1.5px)`,
            background: markerColor,
          }}
        />
      )}

      {/* Nothing measured: a dashed rule reads as "no data" rather than as
          zero, which a marker parked at the left edge would imply. */}
      {markerPct === null && (
        <div
          className="absolute inset-x-2"
          style={{
            top: 4 + trackHeight / 2,
            borderTop: "1px dashed var(--color-status-missing)",
          }}
        />
      )}
    </div>
  );
}
